import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../../context/AuthContext";
import { IndosbookLogo } from "../IndosbookLogo";
import { Button } from "../ui/button";
import { Loader2, LogOut, ShieldCheck } from "lucide-react";
import AdminUsers from "./AdminUsers";
import { AdminPosts, AdminCommunities } from "./AdminContent";
import GrowthChart from "./GrowthChart";
import AdminPartners from "./AdminPartners";

const TABS = [
  { id: "users", label: "Users" },
  { id: "posts", label: "Posts" },
  { id: "jobs", label: "Jobs" },
  { id: "communities", label: "Communities" },
  { id: "partners", label: "Home Badges" },
];

const STAT_ITEMS = [
  ["users", "Total users"], ["new_7d", "New this week"], ["seafarers", "Seafarers"], ["companies", "Companies"],
  ["institutes", "Institutes"], ["blocked", "Blocked"], ["posts", "Posts"], ["jobs", "Jobs"],
  ["applications", "Applications"], ["communities", "Communities"],
];

const Stats = ({ stats }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="admin-stats">
    {STAT_ITEMS.map(([k, label]) => (
      <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-stat-${k}`}>
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</div>
        <div className={`mt-1 text-2xl font-extrabold ${k === "users" ? "text-[#FF4500]" : "text-slate-900"}`}>{stats ? stats[k] : "–"}</div>
      </div>
    ))}
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("users");
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (loading) return;
    if (user?.role !== "admin") { navigate("/admin", { replace: true }); return; }
    loadStats();
  }, [user, loading, navigate, loadStats]);

  if (loading || user?.role !== "admin") {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const signOut = async () => { await logout(); navigate("/admin", { replace: true }); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-[1300px] items-center gap-3 px-4 sm:px-6">
          <IndosbookLogo size={26} />
          <span className="flex items-center gap-1 rounded-full bg-[#002855] px-3 py-1 text-xs font-bold text-white"><ShieldCheck className="h-3.5 w-3.5" /> Admin</span>
          <span className="ml-auto hidden text-sm text-slate-500 sm:block">{user.email}</span>
          <Button variant="outline" onClick={signOut} data-testid="admin-logout-button" className="h-9 rounded-full font-bold">
            <LogOut className="mr-1.5 h-4 w-4" /> Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-[1300px] space-y-6 px-4 py-8 sm:px-6">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 sm:text-3xl" style={{ fontFamily: '"Poppins", sans-serif' }}>Platform overview</h1>
          <p className="mt-1 text-sm text-slate-500">Everyone who has registered on Indosbook, and everything they've shared.</p>
        </div>
        <Stats stats={stats} />
        <GrowthChart refreshKey={stats?.users} />

        <div className="flex flex-wrap gap-2" role="tablist">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${tab === t.id ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "users" && <AdminUsers onChanged={loadStats} />}
        {tab === "posts" && <AdminPosts key="discussion" type="discussion" onChanged={loadStats} />}
        {tab === "jobs" && <AdminPosts key="job" type="job" onChanged={loadStats} />}
        {tab === "communities" && <AdminCommunities onChanged={loadStats} />}
        {tab === "partners" && <AdminPartners />}
      </main>
    </div>
  );
}
